import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Row} from 'react-bootstrap';
import {FmlxAccordion, FmlxIcon} from 'fmlx-common-ui';
import {useDispatch, useSelector} from 'react-redux';
import {ProtocolExplorerAccordionGroup} from 'constants/protocolExplorerEnum';
import {DoubleClickDiv} from '../../../components';
import './ListView.scss';
import ProtocolTooltipWrapper from '../ProtocolTooltipWrapper';
import ProtocolStatus from '../ProtocolStatus';
import {setProtocolExplorerExpandedGroups} from '../../../stores/actions/protocolExplorerAction';
import createSelectableItem from '../../../components/SelectableArea/createSelectableItem';
import useDragDropProtocol from '../useDragDropProtocol';
import {getExpandedItemsArr} from '../../../stores/selectors/protocolExplorerSelectors';
import {
  getProtocolIcon,
  getExplorerItemDataAttribute,
  EXPLORER_ITEM_CLASSNAME,
  isProtocolOwner,
  protocolExplorerHelper,
} from '../../../lib/explorerHelper';
import {getIsOperator, getSessionUser} from '../../../stores/selectors/sessionSelectors';
import ProtocolExplorerFavoriteIndicator from '../ProtocolExplorerFavoriteIndicator';

const ListViewItem = createSelectableItem(
  ({
    protocol,
    isRunning,
    greyedOut,
    selectableRef,
    onBeginDrag,
    onEndDrag,
    onDropped,
    isDragMode,
    isOperatorRoleLoggedIn,
    dataID,
  }) => {
    const {userId} = useSelector(getSessionUser);
    const protocolOwner = isProtocolOwner(protocol, isOperatorRoleLoggedIn, userId);
    const isDraggable = protocolExplorerHelper.checkIsItemDraggable(protocol, userId);
    const isShadowFavorite = protocol.groupView !== ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS;

    const {drag, drop, isDragOver} = useDragDropProtocol({
      protocol,
      onBeginDrag,
      onEndDrag,
      onDropped,
      isRunning,
      disableDrag: isOperatorRoleLoggedIn || !isDraggable,
    });

    return (
      <div
        ref={(e) => {
          drag(e);
          drop(e);
          selectableRef(e);
        }}
        className={[
          'item',
          EXPLORER_ITEM_CLASSNAME,
          `${protocol.isSelected ? 'selected' : ''}`,
          `${protocol.isSelected && isDragMode ? 'dragged' : ''}`,
          `${isDragOver ? 'drag-over' : ''}`,
        ].join(' ')}
        data-id={dataID}
      >
        <ProtocolTooltipWrapper item={protocol} className="tool-tip">
          <div className="protocol-icon">
            <FmlxIcon
              name={getProtocolIcon(protocol, isOperatorRoleLoggedIn, protocolOwner)}
              size="md"
              customColor={greyedOut ? '#B4B4B4' : '#414141'}
            />
            <ProtocolStatus protocol={protocol} isRunning={isRunning} />
          </div>

          <div className={`protocol-name-wrapper protocol-name${greyedOut ? ' greyed-out' : ''}`}>
            {protocol.isFavorite && <ProtocolExplorerFavoriteIndicator greyedOut={greyedOut || isShadowFavorite} />}
            {protocol.name}
          </div>
        </ProtocolTooltipWrapper>
      </div>
    );
  },
);

ListViewItem.propTypes = {
  protocol: PropTypes.object.isRequired,
  isRunning: PropTypes.bool.isRequired,
  trayType: PropTypes.string.isRequired,
  isOperatorRoleLoggedIn: PropTypes.bool.isRequired,
  dataID: PropTypes.string.isRequired,
};

function ListView({
  protocols,
  folders,
  favoriteProtocols,
  onClickProtocol,
  onDoubleClickProtocol,
  greyedOutProtocolIds,
  onCheckIsRunning,
  onProtocolDropped,
  itemsRef,
}) {
  const dispatch = useDispatch();
  const isOperatorRoleLoggedIn = useSelector(getIsOperator);
  const expandedItems = useSelector((state) =>
    getExpandedItemsArr(state, favoriteProtocols.length, folders.length, protocols.length),
  );

  const handleChangeAccordion = (event, index, newExpanded) => {
    const items = {folders, protocols, favoriteProtocols};
    const newExpandedGroup = protocolExplorerHelper.handleAccordionClickAndGetExpandStatus(index, newExpanded, items);

    dispatch(setProtocolExplorerExpandedGroups(newExpandedGroup));
  };

  const [isDragMode, setIsDragMode] = useState(false);

  const handleBeginDrag = (protocol) => {
    if (protocol && !protocol.isSelected) {
      onClickProtocol(protocol);
    }
    setIsDragMode(true);
  };

  const handleEndDrag = () => {
    setIsDragMode(false);
  };

  let counter = 0;
  const generateContent = (items) => {
    const elements = items.map((protocol) => {
      const currentCounter = counter;
      counter++;
      return (
        <Row key={protocol.ID}>
          <DoubleClickDiv
            onClick={() => onClickProtocol(protocol)}
            onDoubleClick={() => onDoubleClickProtocol(protocol)}
          >
            <ListViewItem
              ref={(el) => {
                if (!el?.node) return;

                itemsRef.current[currentCounter] = el.node;
              }}
              protocol={protocol}
              selectableKey={protocol.ID}
              selectableGroupItem={protocol}
              selected={protocol.isSelected}
              greyedOut={greyedOutProtocolIds.includes(protocol.ID)}
              isRunning={onCheckIsRunning(protocol[protocol.type].id, protocol.type)}
              onBeginDrag={handleBeginDrag}
              onEndDrag={handleEndDrag}
              onDropped={onProtocolDropped}
              isDragMode={isDragMode}
              isOperatorRoleLoggedIn={isOperatorRoleLoggedIn}
              dataID={getExplorerItemDataAttribute(protocol, currentCounter)}
            />
          </DoubleClickDiv>
        </Row>
      );
    });

    return {
      count: items.length,
      content: <div className="protocol-container-list protocol-explorer-list-view">{elements}</div>,
    };
  };

  const getItems = () => {
    const param = {
      [ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS]: generateContent(favoriteProtocols),
      [ProtocolExplorerAccordionGroup.FOLDERS]: generateContent(folders),
      [ProtocolExplorerAccordionGroup.PROTOCOLS]: generateContent(protocols),
    };

    const accordionItems = protocolExplorerHelper.generateAccordionItems(param);
    return accordionItems;
  };

  return (
    <FmlxAccordion
      id="accordion-protocol-explorer-list-view"
      multipleExpand
      expandedItems={expandedItems}
      onChange={handleChangeAccordion}
      iconPlacement="left"
      items={getItems()}
    />
  );
}

ListView.propTypes = {
  protocols: PropTypes.array,
  folders: PropTypes.array,
  favoriteProtocols: PropTypes.array,
  greyedOutProtocolIds: PropTypes.array,
  onClickProtocol: PropTypes.func,
  onDoubleClickProtocol: PropTypes.func,
  onCheckIsRunning: PropTypes.func,
  onProtocolDropped: PropTypes.func,
  itemsRef: PropTypes.object,
};

ListView.defaultProps = {
  protocols: [],
  folders: [],
  favoriteProtocols: [],
  greyedOutProtocolIds: [],
  itemsRef: {},
  onClickProtocol: () => {},
  onDoubleClickProtocol: () => {},
  onCheckIsRunning: () => {},
  onProtocolDropped: () => {},
};

export default ListView;
